import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

const STORAGE_KEY = 'hub-dev-log-panel:v1';
const TRACE_URL = '/api/dev/agent-trace';
const MAX_ENTRIES = 600;

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function defaultPosition() {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  return {
    x: clamp(w - 504, 24, w - 100),
    y: 80,
    width: 480,
    height: 520,
  };
}

export function DevLogPanel() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(() => ({
    ...defaultPosition(),
    ...(loadState() || {}),
  }));
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState('');
  const [paused, setPaused] = useState(false);
  const [connected, setConnected] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      if (k === ';' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (k === 'escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    saveState(pos);
  }, [pos]);

  useEffect(() => {
    if (!open) return undefined;
    let es;
    let stopped = false;
    let retryDelay = 1500;
    const connect = () => {
      if (stopped) return;
      try {
        es = new EventSource(TRACE_URL);
      } catch {
        setTimeout(connect, retryDelay);
        return;
      }
      es.addEventListener('open', () => {
        setConnected(true);
        retryDelay = 1500;
      });
      es.addEventListener('snapshot', (event) => {
        try {
          const arr = JSON.parse(event.data);
          if (Array.isArray(arr)) {
            setEntries(arr.slice(-MAX_ENTRIES));
          }
        } catch {
          // ignore parse errors
        }
      });
      es.addEventListener('trace', (event) => {
        try {
          const entry = JSON.parse(event.data);
          setEntries((prev) => {
            const next = [...prev, entry];
            return next.length > MAX_ENTRIES ? next.slice(next.length - MAX_ENTRIES) : next;
          });
        } catch {
          // ignore parse errors
        }
      });
      es.addEventListener('error', () => {
        setConnected(false);
        try { es.close(); } catch {
          // ignore
        }
        if (!stopped) {
          setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 15000);
        }
      });
    };
    connect();
    return () => {
      stopped = true;
      if (es) {
        try { es.close(); } catch {
          // ignore
        }
      }
      setConnected(false);
    };
  }, [open]);

  useEffect(() => {
    if (paused) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, paused]);

  const onDragStart = useCallback((event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    setPos((startPos) => {
      const onMove = (ev) => {
        setPos({
          ...startPos,
          x: clamp(startPos.x + (ev.clientX - startX), 4, window.innerWidth - 80),
          y: clamp(startPos.y + (ev.clientY - startY), 4, window.innerHeight - 60),
        });
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return startPos;
    });
  }, []);

  const onResizeStart = useCallback((event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    setPos((startPos) => {
      const onMove = (ev) => {
        setPos({
          ...startPos,
          width: clamp(startPos.width + (ev.clientX - startX), 320, 1000),
          height: clamp(startPos.height + (ev.clientY - startY), 220, window.innerHeight - 40),
        });
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return startPos;
    });
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((entry) => {
      if (entry.type && entry.type.toLowerCase().includes(q)) return true;
      try {
        return JSON.stringify(entry.payload || {}).toLowerCase().includes(q);
      } catch {
        return false;
      }
    });
  }, [entries, filter]);

  const groups = useMemo(() => {
    const out = [];
    let current = null;
    for (const entry of filtered) {
      const rid = entry.request_id || '_orphan';
      if (!current || current.requestId !== rid) {
        current = { requestId: rid, entries: [] };
        out.push(current);
      }
      current.entries.push(entry);
    }
    return out;
  }, [filtered]);

  if (!open) return null;

  const panel = (
    <div
      className="hub-dev-log-panel"
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: pos.width,
        height: pos.height,
        zIndex: 10100,
      }}
      role="dialog"
      aria-label="Agent trace"
    >
      <div className="hub-dev-log-header" onMouseDown={onDragStart}>
        <span className="hub-dev-log-title">
          <span className={`hub-dev-log-dot${connected ? ' is-on' : ''}`} aria-hidden="true" />
          Agent trace
        </span>
        <input
          type="text"
          className="hub-dev-log-filter"
          placeholder="filter…"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          onMouseDown={(event) => event.stopPropagation()}
        />
        <button
          type="button"
          className={`hub-dev-log-btn${paused ? ' is-on' : ''}`}
          onClick={() => setPaused((v) => !v)}
          onMouseDown={(event) => event.stopPropagation()}
          title={paused ? 'Resume autoscroll' : 'Pause autoscroll'}
        >
          {paused ? '▶' : '❚❚'}
        </button>
        <button
          type="button"
          className="hub-dev-log-btn"
          onClick={() => setEntries([])}
          onMouseDown={(event) => event.stopPropagation()}
          title="Clear"
        >
          ⌫
        </button>
        <button
          type="button"
          className="hub-dev-log-btn"
          onClick={() => setOpen(false)}
          onMouseDown={(event) => event.stopPropagation()}
          title="Close (⌘;)"
        >
          ✕
        </button>
      </div>
      <div className="hub-dev-log-list" ref={listRef}>
        {groups.length === 0 && (
          <div className="hub-dev-log-empty">
            No trace entries yet — submit a chat request to populate.
          </div>
        )}
        {groups.map((group, index) => {
          const head = group.entries.find((entry) => entry.type === 'request_received');
          const userText = head?.payload?.user_text || '';
          return (
            <div key={`${group.requestId}-${index}`} className="hub-dev-log-group">
              {head ? (
                <div
                  className={`hub-dev-log-group-head${head.payload?.has_tool_outputs ? ' is-tool-output' : ''}`}
                >
                  <span className="hub-dev-log-group-mark" aria-hidden="true">
                    {head.payload?.has_tool_outputs ? '↳' : '▼'}
                  </span>
                  <span className="hub-dev-log-group-text">
                    {head.payload?.has_tool_outputs
                      ? 'tool-output continuation'
                      : userText
                        ? userText.slice(0, 240)
                        : '(empty prompt)'}
                  </span>
                  <span className="hub-dev-log-group-id">
                    #{String(group.requestId).slice(0, 6)}
                  </span>
                </div>
              ) : null}
              {group.entries.map((entry) => (
                <DevLogEntry
                  key={entry.id}
                  entry={entry}
                  hideRequestText={Boolean(head)}
                />
              ))}
            </div>
          );
        })}
      </div>
      <div
        className="hub-dev-log-resize"
        onMouseDown={onResizeStart}
        aria-hidden="true"
      />
    </div>
  );

  return createPortal(panel, document.body);
}

function DevLogEntry({ entry, hideRequestText }) {
  const ts = new Date(entry.ts || Date.now());
  const hh = String(ts.getHours()).padStart(2, '0');
  const mm = String(ts.getMinutes()).padStart(2, '0');
  const ss = String(ts.getSeconds()).padStart(2, '0');
  const ms = String(ts.getMilliseconds()).padStart(3, '0');
  return (
    <div className={`hub-dev-log-row hub-dev-log-row-${entry.type}`}>
      <span className="hub-dev-log-time">{hh}:{mm}:{ss}.{ms}</span>
      <span className="hub-dev-log-type">{entry.type}</span>
      <span className="hub-dev-log-payload">
        {summarizePayload(entry.type, entry.payload || {}, hideRequestText)}
      </span>
    </div>
  );
}

function summarizePayload(type, payload, hideRequestText) {
  switch (type) {
    case 'request_received':
      if (hideRequestText) {
        return payload.has_tool_outputs ? '(with tool outputs)' : '';
      }
      return payload.user_text || '(no text)';
    case 'planner_decided':
      return [payload.tool, payload.route, payload.reason]
        .filter((part) => part)
        .join(' · ');
    case 'tool_dispatched':
      return [payload.tool, payload.text].filter((part) => part).join(' · ');
    case 'self_terminate':
      return `${payload.tool || ''} · ${payload.reason || ''}`;
    case 'openclaw_handoff_start':
      return `mode=${payload.mode || '?'}${payload.desktop_required ? ' (desktop)' : ''}`;
    case 'openclaw_handoff_end':
      if (payload.ok) {
        const action = payload.action ? `${payload.action} · ` : '';
        return `ok · ${action}${(payload.text_excerpt || '').slice(0, 200)}`;
      }
      return `FAIL · ${payload.error || ''}`;
    default:
      try {
        return JSON.stringify(payload);
      } catch {
        return '';
      }
  }
}
